import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, Search, ChevronLeft, ChevronRight, Upload, Building2,
  KeyRound, CheckCircle2, FileUp, Users, X, FileText, MessagesSquare,
  LayoutDashboard, Building, Wrench,
} from 'lucide-react';
import { theme } from '../shared/theme';
import { portalFetch } from '../shared/api';
import { EA_CUSTOMERS, EACustomer, EA_UPLOAD_TYPES } from '../shared/mockData';
import TenantPortal from '../tenant/TenantPortal';
import PropertyManagementView from '../owner/PropertyManagementView';
import AdminInbox from './AdminInbox';
import ConsoleFrame, { CONSOLE_URLS } from './ConsoleFrame';
import AdminIssues from './AdminIssues';
import AdminProperties from './AdminProperties';
import AdminAssetDesk from './AdminAssetDesk';
import AdminAccessLog from './AdminAccessLog';
import { fetchMessageSummary, pollWhileActive } from '../shared/messaging';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

const mapDoc = (d: any) => ({
  id: d.id, name: d.name,
  type: d.documentType || d.type || 'Document',
  date: d.date || '',
});

// ============================================================
// EAAdminConsole — the internal-staff (Marilyn & team) overview.
// This is NOT a separate role/button. It is shown as the "overview"
// whenever a staff/admin account logs in through a normal role
// (Owner / Tenant / Guest). Staff can search customers, switch into
// a customer's view, and upload tenancy contracts, inventory lists,
// security & rent cheques on the customer's behalf.
//
// Frontend-first: mock customer list. Real impersonation + upload
// endpoints get wired once accounts are provided.
// ============================================================

const ACCENT = theme.gold;

export type ConsoleView = 'assetdesk' | 'pm' | 'chat' | 'issues' | 'access' | 'customers';

interface Props {
  staffName?: string;
  /** Prefills the Asset Desk sign-in; it is the account already signed in. */
  staffEmail?: string;
  roleLabel?: string; // e.g. "Owner workspace" / "Tenant workspace"
  onExit?: () => void;
  onViewOwn?: () => void; // optional: let staff view their own (non-admin) overview
  /**
   * CONTROLLED MODE. When the shell owns the navigation — the owner app puts
   * these four destinations in its own side rail for an admin — it passes the
   * current one here and the console drops its internal tab row. Two rows of
   * tabs for one set of destinations is the stacking this console exists to
   * avoid. Left undefined, the console navigates itself, which is what the
   * tenant portal still needs.
   */
  view?: ConsoleView;
  /** Unread conversations, so the shell can badge its own Chat entry. */
  onUnreadChange?: (n: number) => void;
}

const EAAdminConsole: React.FC<Props> = ({ staffName = 'Staff', staffEmail, roleLabel, onExit, onViewOwn, view: viewProp, onUnreadChange }) => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState<EACustomer | null>(null);
  const [impersonate, setImpersonate] = useState<{ mode: 'tenant' | 'owner'; customer: EACustomer } | null>(null);
  // THREE destinations, the three things the team actually does here: Asset
  // Desk, Property Management, and the owner conversations. The first two are
  // the existing internal consoles, embedded (see ConsoleFrame); the third is
  // native.
  //
  // Asset Desk lands first because that is where the day starts. Chat does
  // not have to be first to be noticed: its tab carries the unread count, and
  // that count keeps polling whichever tab is open.
  //
  // 'customers' is still a reachable view but no longer has a tab. It listed
  // EA_CUSTOMERS — mock people — anywhere the /ea/customers endpoint is
  // missing, which is production; the two consoles above show the real ones.
  const [ownView, setOwnView] = useState<ConsoleView>('assetdesk');
  const controlled = viewProp !== undefined;
  const view = controlled ? viewProp : ownView;
  const setView = setOwnView;
  const [unread, setUnread] = useState(0);
  // A conversation has the screen — the console tab bar steps aside for it.
  const [threadOpen, setThreadOpen] = useState(false);

  // The badge on the Messages tab, so a manager parked on the customer list
  // still sees a new message arrive. One cheap call; the inbox itself polls
  // properly once it is open.
  useEffect(() => {
    let alive = true;
    const poll = () => {
      fetchMessageSummary()
        .then((s) => { if (alive) { setUnread(s.unread || 0); onUnreadChange?.(s.unread || 0); } })
        .catch(() => { /* badge only — never worth an error on screen */ });
    };
    poll();
    const stop = pollWhileActive(poll, 30000);
    return () => { alive = false; stop(); };
  }, [onUnreadChange]);

  // "View as" — open the real customer app (reads the shared store).
  if (impersonate) {
    return (
      <ImpersonationFrame onBack={() => setImpersonate(null)}>
        {impersonate.mode === 'tenant'
          ? <TenantPortal previewEmail={impersonate.customer.email} />
          : <OwnerImpersonation customer={impersonate.customer} />}
      </ImpersonationFrame>
    );
  }

  return (
    <div className="pt-screen ea-console" style={{ paddingBottom: 24, minHeight: 'auto' }}>
      {/* Dark staff header */}
      <header
        style={{
          background: theme.navy,
          padding: '16px 16px calc(16px + env(safe-area-inset-top, 0px))',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ShieldCheck size={22} color={ACCENT} />
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>{t('eaConsoleTitle')}</div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)' }}>
              {staffName}{roleLabel ? ` · ${roleLabel}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* ONE way out to the full console, in the chrome, rather than a
              button on top of every screen. The screens below are the app;
              this is for the handful of things they deliberately do not do
              (uploads, adding a unit, verifying a document). */}
          <a
            href={CONSOLE_URLS.propertyManagement}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...hdrBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            {t('adFullConsole')}
          </a>
          {onViewOwn && (
            <button onClick={onViewOwn} style={hdrBtn}>{t('eaMyView')}</button>
          )}
          {onExit && (
            <button onClick={onExit} style={hdrBtn}>{t('eaExit')}</button>
          )}
        </div>
      </header>

      {/* The tab bar steps aside for a customer detail screen and for an open
          conversation — both bring their own header, and two stacked rows of
          chrome over one thing is a row nobody asked for. */}
      {!controlled && !selected && !threadOpen && (
        <div className="ea-console-body" style={{ display: 'flex', gap: 8, padding: '12px 14px 0', overflowX: 'auto' }}>
          <ConsoleTab
            active={view === 'assetdesk'}
            icon={LayoutDashboard}
            label={t('eaTabAssetDesk')}
            onClick={() => setView('assetdesk')}
          />
          <ConsoleTab
            active={view === 'pm'}
            icon={Building}
            label={t('eaTabPm')}
            onClick={() => setView('pm')}
          />
          <ConsoleTab
            active={view === 'chat'}
            icon={MessagesSquare}
            label={t('eaTabChat')}
            badge={unread}
            onClick={() => setView('chat')}
          />
          <ConsoleTab
            active={view === 'issues'}
            icon={Wrench}
            label={t('eaTabIssues')}
            onClick={() => setView('issues')}
          />
          <ConsoleTab
            active={view === 'access'}
            icon={ShieldCheck}
            label={t('eaTabAccess')}
            onClick={() => setView('access')}
          />
        </div>
      )}

      <div className="ea-console-body">
      {selected ? (
        <CustomerDetail
          customer={selected}
          onBack={() => setSelected(null)}
          onImpersonate={(mode) => setImpersonate({ mode, customer: selected })}
        />
      ) : view === 'chat' ? (
        <AdminInbox
          staffName={staffName}
          onUnreadChange={(n) => { setUnread(n); onUnreadChange?.(n); }}
          onFocusChange={setThreadOpen}
        />
      ) : view === 'assetdesk' ? (
        /* Asset Desk, native. It needs its own sign-in because its endpoints
           take an admin JWT and the app session is not one — same password,
           same allow-list, just entered here instead of in another window. */
        <AdminAssetDesk staffEmail={staffEmail} />
      ) : view === 'pm' ? (
        /* Property Management, rebuilt natively rather than framed. The
           browser refuses to frame the console and the fix for that is not
           ours to make, so the screens moved here instead — which is where
           they were wanted. */
        <AdminProperties />
      ) : view === 'issues' ? (
        <AdminIssues />
      ) : view === 'access' ? (
        /* Staff access log — who opened which investor's data (Trust Center). */
        <AdminAccessLog />
      ) : view === 'customers' ? (
        <CustomerList onSelect={setSelected} />
      ) : null}
      </div>

      {/* OUTSIDE .ea-console-body on purpose: these two are whole consoles and
          want every pixel of the window, not the reading measure the lists
          above are held to. */}



    </div>
  );
};

const ConsoleTab: React.FC<{
  active: boolean;
  icon: React.ComponentType<any>;
  label: string;
  badge?: number;
  onClick: () => void;
}> = ({ active, icon: Icon, label, badge, onClick }) => (
  <button
    onClick={onClick}
    className="pt-tap"
    style={{
      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
      background: active ? theme.navy : '#fff',
      color: active ? '#fff' : theme.slate,
      border: `1px solid ${active ? theme.navy : theme.line}`,
      borderRadius: 12, padding: '10px 12px', minHeight: 0,
      fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
    }}
  >
    <Icon size={16} /> {label}
    {!!badge && badge > 0 && <span className="pm-msg-dot">{badge > 99 ? '99+' : badge}</span>}
  </button>
);

// Full-screen wrapper for "view as": renders the real customer app with a
// floating control to return to the console (kept clear of the app's own
// header and bottom nav).
const ImpersonationFrame: React.FC<{ onBack: () => void; children: React.ReactNode }> = ({ onBack, children }) => {
  const { t } = useLanguage();
  return (
  <>
    {children}
    <button
      onClick={onBack}
      style={{
        position: 'fixed', bottom: 76, left: 12, zIndex: 70,
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: theme.navy, color: '#fff', border: 'none', borderRadius: 99,
        padding: '9px 15px', fontSize: 12.5, fontWeight: 700,
        boxShadow: '0 6px 18px rgba(0,0,0,0.25)', cursor: 'pointer',
      }}
    >
      <ChevronLeft size={15} /> {t('eaExitViewAs')}
    </button>
  </>
  );
};

const OwnerImpersonation: React.FC<{ customer: EACustomer }> = ({ customer }) => {
  const { t } = useLanguage();
  return (
  <div className="pt-screen" style={{ background: '#f4f6f9' }}>
    <div style={{ padding: '18px 16px 0' }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: '#12294a', margin: 0 }}>{t('propertyManagement')}</h1>
      <p style={{ fontSize: 14, color: '#3f4a5a', marginTop: 4 }}>{t('eaViewingAs')} {customer.name}</p>
    </div>
    <PropertyManagementView currentInvestor={{ id: customer.id, name: customer.name, email: customer.email }} />
  </div>
  );
};

const hdrBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 10,
  padding: '8px 12px', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
};

// ---------------- Customer list ----------------
const CustomerList: React.FC<{ onSelect: (c: EACustomer) => void }> = ({ onSelect }) => {
  const { t } = useLanguage();
  const [q, setQ] = useState('');
  const [customers, setCustomers] = useState<EACustomer[]>(EA_CUSTOMERS);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { owners, tenants } = await portalFetch<any>('/ea/customers');
        const mapped: EACustomer[] = [
          ...(owners || []).map((o: any) => ({
            id: o.id, name: o.name, email: o.email,
            units: o.units || 1, role: 'owner' as const, pmActive: !!o.pmActive,
          })),
          ...(tenants || []).map((t: any) => ({
            id: t.id, name: t.name, email: t.email,
            units: 1, role: 'tenant' as const, pmActive: true,
          })),
        ];
        if (alive && mapped.length) setCustomers(mapped);
      } catch {
        /* dev API not running → keep mock list */
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = customers.filter(
    (c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.email.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div className="pt-fade" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, background: '#fff', border: `1px solid ${theme.line}`, borderRadius: 12, padding: '10px 12px' }}>
        <Search size={18} color={theme.slateLight} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('eaSearchCustomers')} style={{ border: 'none', outline: 'none', fontSize: 14.5, flex: 1, background: 'transparent' }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 4px 10px', color: theme.slate, fontSize: 12.5, fontWeight: 600 }}>
        <Users size={15} /> {filtered.length} {t('eaCustomers')}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.map((c) => (
          <button key={c.id} onClick={() => onSelect(c)} className="pt-card pt-tap" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, border: 'none', textAlign: 'left', width: '100%' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: c.role === 'owner' ? '#fdf6e6' : theme.infoBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {c.role === 'owner' ? <Building2 size={20} color={ACCENT} /> : <KeyRound size={20} color={theme.info} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.ink }}>{c.name}</div>
              <div style={{ fontSize: 12.5, color: theme.slate }}>{c.email}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                <span style={pill('#f3f4f6', theme.slate)}>{c.role === 'owner' ? `${c.units} ${c.units > 1 ? t('eaUnits') : t('eaUnit')}` : t('eaTenant')}</span>
                {c.pmActive && <span style={pill(theme.successBg, theme.success)}>{t('eaPmActive')}</span>}
              </div>
            </div>
            <ChevronRight size={20} color={theme.slateLight} />
          </button>
        ))}
      </div>
    </div>
  );
};

// ---------------- Customer detail (view as + uploads) ----------------
const CustomerDetail: React.FC<{ customer: EACustomer; onBack: () => void; onImpersonate: (mode: 'tenant' | 'owner') => void }> = ({ customer, onBack, onImpersonate }) => {
  const { t } = useLanguage();
  const [unitId, setUnitId] = useState<string | null>(null);
  const [unitOptions, setUnitOptions] = useState<{ id: string; name: string }[]>([]);
  const [documents, setDocuments] = useState<{ id: string; name: string; type: string; date: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [saving, setSaving] = useState(false);

  // Resolve this customer's unit context + real documents from the store.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        if (customer.role === 'tenant') {
          const b = await portalFetch<any>('/tenant/login', { method: 'POST', body: JSON.stringify({ email: customer.email }) });
          if (!alive) return;
          const uid = b.unit?.id || b.tenant?.unitId || null;
          setUnitId(uid);
          setUnitOptions(b.unit ? [{ id: b.unit.id, name: b.unit.name || uid }] : []);
          setDocuments((b.documents || []).map(mapDoc));
        } else {
          const { units } = await portalFetch<any>(`/pm/owner/${customer.id}/units`);
          if (!alive) return;
          const opts = (units || []).map((u: any) => ({ id: u.id, name: u.name || u.id }));
          setUnitOptions(opts);
          setUnitId(opts[0]?.id || null);
        }
      } catch { /* dev API may be down → leave empty */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [customer]);

  // Owner: load the selected unit's documents when the unit changes.
  useEffect(() => {
    if (customer.role !== 'owner' || !unitId) return;
    let alive = true;
    (async () => {
      try {
        const { documents } = await portalFetch<any>(`/pm/unit/${unitId}`);
        if (alive) setDocuments((documents || []).map(mapDoc));
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [unitId, customer.role]);

  const refreshDocs = useCallback(async () => {
    try {
      if (customer.role === 'tenant') {
        const b = await portalFetch<any>('/tenant/login', { method: 'POST', body: JSON.stringify({ email: customer.email }) });
        setDocuments((b.documents || []).map(mapDoc));
      } else if (unitId) {
        const { documents } = await portalFetch<any>(`/pm/unit/${unitId}`);
        setDocuments((documents || []).map(mapDoc));
      }
    } catch { /* ignore */ }
  }, [customer, unitId]);

  // Persist an uploaded document to the shared store → shows up in the
  // owner + tenant apps (metadata only, matching the admin page).
  const handleUpload = async (item: { type: string; name: string }) => {
    setShowUpload(false);
    if (!unitId) return;
    setSaving(true);
    try {
      await portalFetch(`/pm/unit/${unitId}/document`, { method: 'POST', body: JSON.stringify({ name: item.name, type: item.type }) });
      await refreshDocs();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const roleLabel = customer.role === 'owner' ? t('eaPropertyOwner') : t('eaTenant');

  return (
    <div className="pt-fade" style={{ padding: 16 }}>
      <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: theme.slate, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', marginBottom: 14 }}>
        <ChevronLeft size={16} /> {t('eaAllCustomers')}
      </button>

      {/* Customer header */}
      <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: customer.role === 'owner' ? '#fdf6e6' : theme.infoBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {customer.role === 'owner' ? <Building2 size={24} color={ACCENT} /> : <KeyRound size={24} color={theme.info} />}
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: theme.ink }}>{customer.name}</div>
            <div style={{ fontSize: 13, color: theme.slate }}>{customer.email}</div>
          </div>
        </div>
      </div>

      {/* View-as */}
      <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink, marginBottom: 4 }}>{t('eaOpenAsCustomer')}</div>
        <p style={{ fontSize: 12.5, color: theme.slate, margin: '0 0 12px' }}>{t('eaSeeExactly1')} {customer.name.split(' ')[0]} {t('eaSeeExactly2')}</p>
        <button
          onClick={() => onImpersonate(customer.role === 'owner' ? 'owner' : 'tenant')}
          className="pt-tap"
          style={{ ...viewAsBtn, width: '100%' }}
        >
          {customer.role === 'owner' ? <Building2 size={18} color={ACCENT} /> : <KeyRound size={18} color={theme.info} />}
          {`${t('eaOpenAppPre')}${roleLabel}${t('eaOpenAppPost')}`}
        </button>
      </div>

      {/* Owner unit picker (act on a specific unit) */}
      {customer.role === 'owner' && unitOptions.length > 0 && (
        <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.ink, marginBottom: 6 }}>{t('unit')}</div>
          <select
            value={unitId || ''}
            onChange={(e) => setUnitId(e.target.value)}
            style={{ width: '100%', padding: '12px 14px', border: `1px solid ${theme.line}`, borderRadius: 12, fontSize: 15, background: '#f9fafb' }}
          >
            {unitOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}

      {/* Documents (real, from the shared store) */}
      <div className="pt-card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink }}>{t('eaDocsOnFile')}</div>
          <button
            onClick={() => setShowUpload(true)}
            disabled={!unitId || saving}
            className="pt-tap"
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: ACCENT, color: theme.navy, border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, opacity: !unitId ? 0.5 : 1 }}
          >
            {saving ? <span className="pt-spin" style={{ borderTopColor: theme.navy }} /> : <><Upload size={15} /> {t('upload')}</>}
          </button>
        </div>
        {loading && <p style={{ fontSize: 13, color: theme.slate, textAlign: 'center', padding: 16 }}>{t('loading')}</p>}
        {!loading && !unitId && <p style={{ fontSize: 13, color: theme.slate, textAlign: 'center', padding: 16 }}>{t('eaNoUnitLinked')}</p>}
        {!loading && unitId && documents.length === 0 && <p style={{ fontSize: 13, color: theme.slate, textAlign: 'center', padding: 16 }}>{t('noDocumentsUploaded')}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, border: `1px solid ${theme.line}`, borderRadius: 10 }}>
              <FileText size={18} color={theme.info} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                <div style={{ fontSize: 12, color: theme.slate }}>{d.type}{d.date ? ` · ${d.date}` : ''}</div>
              </div>
              <CheckCircle2 size={16} color={theme.success} />
            </div>
          ))}
        </div>
      </div>

      {showUpload && (
        <UploadSheet
          customerName={customer.name}
          onClose={() => setShowUpload(false)}
          onDone={handleUpload}
        />
      )}
    </div>
  );
};

const viewAsBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  padding: '12px', background: '#f9fafb', border: `1px solid ${theme.line}`,
  borderRadius: 12, fontSize: 13.5, fontWeight: 700, color: theme.ink,
};

// ---------------- Upload sheet ----------------
const UploadSheet: React.FC<{ customerName: string; onClose: () => void; onDone: (item: { type: string; name: string }) => void }> = ({ customerName, onClose, onDone }) => {
  const { t } = useLanguage();
  const [type, setType] = useState(EA_UPLOAD_TYPES[0]);
  const [file, setFile] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const submit = () => {
    setBusy(true);
    setTimeout(() => {
      onDone({ type, name: file || `${type} — ${customerName}.pdf` });
    }, 800);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
      <div className="pt-fade" style={{ position: 'relative', width: '100%', maxWidth: 640, background: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: theme.ink, margin: 0 }}>{t('eaUploadDocument')}</h2>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 10, padding: 8, cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <label style={{ fontSize: 13, fontWeight: 600, color: theme.ink }}>{t('documentType')}</label>
        <select value={type} onChange={(e) => setType(e.target.value)} style={{ width: '100%', marginTop: 6, padding: '12px 14px', border: `1px solid ${theme.line}`, borderRadius: 12, fontSize: 15, background: '#f9fafb' }}>
          {EA_UPLOAD_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>

        <label style={{ fontSize: 13, fontWeight: 600, color: theme.ink, marginTop: 14, display: 'block' }}>{t('eaFile')}</label>
        <label style={{ marginTop: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 22, border: `2px dashed ${theme.line}`, borderRadius: 12, cursor: 'pointer', color: theme.slate }}>
          <FileUp size={26} color={theme.slateLight} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{file || t('eaChooseFile')}</span>
          <input type="file" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0]?.name || '')} />
        </label>

        <button onClick={submit} disabled={busy} className="pt-tap" style={{ width: '100%', marginTop: 20, padding: 14, background: ACCENT, color: theme.navy, border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {busy ? <span className="pt-spin" style={{ borderTopColor: theme.navy }} /> : <><Upload size={16} /> {t('eaUploadTo')} {customerName}</>}
        </button>
      </div>
    </div>
  );
};

function pill(bg: string, color: string): React.CSSProperties {
  return { display: 'inline-block', fontSize: 10.5, fontWeight: 700, color, background: bg, padding: '2px 8px', borderRadius: 99 };
}

export default EAAdminConsole;
